import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer, Type } from '@angular/core';
import { first, filter } from 'rxjs';

import { AUTH_PROVIDER } from '../tokens';
import { ConnectionService } from '../../services/connection.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { SplashScreenService } from '../../services/splash-screen.service';

/**
 * Ein benannter Initialisierungsschritt mit Splash-Screen-Meldung.
 *
 * Die `setup()`-Funktion wird im Injection-Context aufgerufen (synchron) und
 * gibt die async `run()`-Funktion zurück. So können Services per `inject()`
 * aufgelöst werden bevor der erste `await` den Context verlässt.
 *
 * @example
 * ```typescript
 * {
 *   label: 'Offline-Karte laden...',
 *   setup: () => {
 *     const mbtiles = inject(MBTilesService);
 *     return () => mbtiles.load('/assets/osm-base.mbtiles');
 *   },
 * }
 * ```
 */
export interface LuzulaInitStep {
	/** Splash-Screen-Meldung die während dieses Steps angezeigt wird */
	label: string;
	/** Factory die im Injection-Context läuft und die async Run-Funktion zurückgibt */
	setup: () => (() => Promise<void>);
}

/**
 * Konfiguration für die App-Initialisierung.
 */
export interface LuzulaInitConfig {
	/**
	 * Schritte die nach dem Offline-Check aber vor Auth laufen.
	 * Jeder Step zeigt seine `label`-Meldung im Splash-Screen.
	 */
	steps?: LuzulaInitStep[];

	/**
	 * Services die eager injiziert werden sollen um deren Construction zu triggern.
	 * Nützlich für Services mit Constructor-Seiteneffekten (z.B. UpdateService, GpsService).
	 */
	eagerServices?: Type<any>[];

	/**
	 * Init-Modus:
	 * - `'standard'` (default): Splash → Offline-Check → Steps → Network-Monitor → Auth
	 * - `'deferred'`: Auth sofort initialisieren, onLoggedIn-Callback nach Login
	 */
	mode?: 'standard' | 'deferred';

	/**
	 * Nur für `mode: 'deferred'`: Wird aufgerufen sobald der User authentifiziert ist.
	 * Typisch: DB-Services initialisieren.
	 */
	onLoggedIn?: () => void;
}

/**
 * Stellt eine standardisierte App-Initialisierung bereit.
 *
 * **Standard-Modus** (bpweb, forsteinrichtung):
 * 1. Splash: "Offline-Einstellungen prüfen..."
 * 2. `OfflineModeService.initFromStorage()`
 * 3. App-spezifische Steps (z.B. MBTiles laden, FGK-API prüfen)
 * 4. Falls offline → `AuthProvider.init(false, true)` → fertig
 * 5. Falls online → Network-Monitor → `AuthProvider.init(online, true)`
 *
 * **Deferred-Modus** (weisergatter):
 * 1. `AuthProvider.init()` sofort
 * 2. `onLoggedIn()` nach erfolgreicher Authentifizierung
 *
 * @example
 * ```typescript
 * // Minimal (forsteinrichtung ohne Steps):
 * provideLuzulaInit()
 *
 * // Mit Steps + Eager Services:
 * provideLuzulaInit({
 *   steps: [{ label: 'Offline-Karte laden...', run: () => mbtiles.load(...) }],
 *   eagerServices: [UpdateService, DataService],
 * })
 *
 * // Deferred (weisergatter):
 * provideLuzulaInit({
 *   mode: 'deferred',
 *   onLoggedIn: () => { ps.initialize(); ds.initialize(); },
 * })
 * ```
 */
export function provideLuzulaInit(config?: LuzulaInitConfig): EnvironmentProviders {
	return makeEnvironmentProviders([
		provideAppInitializer(() => {
			const oms = inject(OfflineModeService);
			const as = inject(AUTH_PROVIDER);

			// Eager-inject konfigurierte Services (triggert deren Konstruktoren)
			config?.eagerServices?.forEach(svc => inject(svc));

			// Steps im Injection-Context vorresolvieren (inject() nur synchron möglich)
			const resolvedSteps = config?.steps?.map(step => ({
				label: step.label,
				run: step.setup()
			}));

			const mode = config?.mode ?? 'standard';

			if (mode === 'deferred') {
				// Deferred-Pattern: Auth sofort, DB-Init nach Login
				as.init(!oms.userOfflineMode(), true);
				if (config?.onLoggedIn) {
					const cb = config.onLoggedIn;
					as.isAuthenticated$().pipe(
						filter(authenticated => authenticated),
						first()
					).subscribe(() => cb());
					if (as.isAuthenticated.getValue() && as.currentUserId) {
						cb();
					}
				}
				return;
			}

			// Standard-Pattern: Splash → Offline → Steps → Network → Auth
			const cs = inject(ConnectionService);
			const splash = inject(SplashScreenService);

			return (async () => {
				splash.updateStatus('Offline-Einstellungen prüfen...');
				const userOfflineMode = await oms.initFromStorage();

				// App-spezifische Steps ausführen
				if (resolvedSteps) {
					for (const step of resolvedSteps) {
						splash.updateStatus(step.label);
						await step.run();
					}
				}

				if (userOfflineMode) {
					as.init(false, true);
					return;
				}

				splash.updateStatus('Netzwerkverbindung prüfen...');
				cs.monitor().pipe(first()).subscribe(online => {
					splash.updateStatus(online ? 'Anmeldung prüfen...' : 'Offline starten...');
					as.init(online, true);
				});
			})();
		})
	]);
}
